import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ExternalLink, ArrowUpRight } from "lucide-react";
import { CATEGORY_LABELS, MONTH_NAMES, TYPE_LABELS } from "../lib/format";

export const DateDetail = ({ entry, onClose }) => {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!entry) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [entry, onClose]);

  if (!entry) return null;

  const monthLabel = entry.month ? MONTH_NAMES[entry.month] : null;
  const dayLabel = entry.day || null;
  const typeLabel = TYPE_LABELS[entry.type] || entry.type;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="date-detail-title"
      data-testid="date-detail-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "hsl(var(--shadow) / 0.55)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "3rem 1.25rem",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <article
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 780,
          background: "hsl(var(--bg))",
          border: "1px solid hsl(var(--line))",
          padding: "2.5rem clamp(1.25rem, 3vw, 2.5rem)",
          position: "relative",
        }}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close detail"
          data-testid="date-detail-close"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 36,
            height: 36,
            borderRadius: 999,
            border: "1px solid hsl(var(--line))",
            background: "hsl(var(--bg-elev))",
            color: "hsl(var(--ink))",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={16} aria-hidden />
        </button>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: "1rem" }}>
          <span className="tag tag--solid">{entry.recognition_status}</span>
          {typeLabel && typeLabel !== entry.recognition_status ? (
            <span className="tag">{typeLabel}</span>
          ) : null}
          {entry.year_established ? (
            <span className="tag" style={{ fontFamily: "var(--font-display)", letterSpacing: "0.02em" }}>
              Since {entry.year_established}
            </span>
          ) : null}
        </div>

        <div
          className="archival-figure"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.95rem",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "hsl(var(--accent))",
          }}
        >
          {monthLabel ? `${monthLabel}${dayLabel ? " " + dayLabel : ""}` : entry.date_label}
        </div>

        <h2
          id="date-detail-title"
          style={{
            marginTop: 8,
            marginBottom: "1rem",
            fontSize: "clamp(1.7rem, 3vw, 2.4rem)",
            lineHeight: 1.1,
          }}
        >
          {entry.title}
        </h2>

        <p style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontSize: "1.15rem",
          lineHeight: 1.55,
          color: "hsl(var(--ink-soft))",
          marginBottom: "1.5rem",
        }}>
          {entry.short_description}
        </p>

        <h3 style={subHeadStyle}>
          Why it matters
        </h3>
        <p style={{ fontSize: "1rem", lineHeight: 1.7, marginBottom: "1.75rem" }}>
          {entry.significance}
        </p>

        {entry.people && entry.people.length > 0 && (
          <>
            <h3 style={subHeadStyle}>Key figures</h3>
            <p style={{ marginBottom: "1.5rem", fontSize: "0.95rem" }}>
              {entry.people.join(" · ")}
            </p>
          </>
        )}

        {entry.categories && entry.categories.length > 0 && (
          <>
            <h3 style={subHeadStyle}>Categories</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: "1.5rem" }}>
              {entry.categories.map((c) => (
                <span key={c} className="tag">{CATEGORY_LABELS[c] || c}</span>
              ))}
            </div>
          </>
        )}

        {entry.suggested_actions && entry.suggested_actions.length > 0 && (
          <>
            <h3 style={subHeadStyle}>Ways to mark the day</h3>
            <ul style={{ margin: 0, paddingLeft: "1.1rem", marginBottom: "1.5rem" }}>
              {entry.suggested_actions.map((a, i) => (
                <li key={i} style={{ fontSize: "0.95rem", lineHeight: 1.5, marginBottom: 4 }}>{a}</li>
              ))}
            </ul>
          </>
        )}

        {entry.sources && entry.sources.length > 0 && (
          <>
            <h3 style={subHeadStyle}>Sources & further reading</h3>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
              {entry.sources.map((s) => (
                <li key={s}>
                  <a href={s} target="_blank" rel="noopener noreferrer" style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontSize: "0.92rem", color: "hsl(var(--accent))",
                  }}>
                    {s.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                    <ExternalLink size={13} aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}

        <div style={{
          marginTop: "2rem",
          paddingTop: "1.5rem",
          borderTop: "1px solid hsl(var(--line))",
          display: "flex",
          justifyContent: "flex-end",
        }}>
          <Link
            to={`/date/${entry.id}`}
            data-testid="read-full-history"
            onClick={onClose}
            className="btn btn--primary"
          >
            Read full history <ArrowUpRight size={14} aria-hidden />
          </Link>
        </div>
      </article>
    </div>
  );
};

const subHeadStyle = {
  fontSize: "0.85rem",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "hsl(var(--muted))",
  marginBottom: "0.5rem",
  fontFamily: "var(--font-body)",
  fontWeight: 600,
};
